import { GsbEntityService } from '../entity/gsb-entity.service';
import { getEnvToken, getGsbTenantCode, setGsbToken } from '../../config/gsb-config';
import { GsbCacheService } from '../cache/gsb-cache.service';
import { GsbFile } from '@gsb-core/core';
import {GsbSendResetPasswordEmailResponse, GsbResponse} from "../../types/responses"
import { SubscriptionType } from '../../models/gsb-subscription.model';
// Storage key prefixes
const TOKEN_PREFIX = 'gsb_auth_';
const USER_DATA_PREFIX = 'gsb_user_';
const ACTIVE_TENANT_KEY = 'gsb_active_tenant';

// Role constants
const ADMIN_ROLE_ID = '59b62a77-b722-44ad-9a2d-478365e8f1a0';

// Cache keys
const CACHE_PREFIX = 'auth_';

// Key types for generating storage keys
type KeyType = 'token' | 'userData' | 'expiry';

// Token refresh settings
const TOKEN_REFRESH_BUFFER = 3 * 60 * 1000; // 3 minutes before expiry
const TOKEN_REFRESH_RETRY_DELAY = 30 * 1000; // 30 seconds between retries
const TOKEN_REFRESH_MAX_RETRIES = 3; // Maximum number of retry attempts

// Singleton instance
let authServiceInstance: AuthService | null = null;

export interface LoginCredentials {
  email: string;
  password?: string;
  remember?: boolean;
  // Social auth tokens
  googleToken?: string;
  facebookToken?: string;
  appleToken?: string;
  tenantCode?: string;
  socialToken?: string;
  socialProvider?: any;
  variation?: any;
  includeUserInfo?: boolean;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  userData?: UserData;
  error?: string;
}

export interface UserData {
  userId?: string;
  name?: string;
  email?: string;
  roles?: string[];
  groups?: string[];
  expireDate?: string;
  title?: string;
  subscription?: SubscriptionType;
  pictureUrl?: string;
  picture?: GsbFile;
}

/**
 * Authentication service for managing auth tokens with GSB backend
 * Single-tenant implementation with forward compatibility for multi-tenant
 */
export class AuthService {
  private gsbService: GsbEntityService;
  private cacheService: GsbCacheService;
  private token: string | null = null;
  private tenantCode?: string;
  private refreshTokenTimeout: NodeJS.Timeout | null = null;
  private refreshTokenRetryCount = 0;

  private constructor() {
    this.gsbService = GsbEntityService.getInstance();
    this.cacheService = GsbCacheService.getInstance();
    this.tenantCode = getGsbTenantCode();
    this.initFromStorage();
  }

  public static getInstance(): AuthService {
    if (!authServiceInstance) {
      authServiceInstance = new AuthService();
    }
    return authServiceInstance;
  }


  private getKeys(tenantCode: string, keyType: KeyType): { storageKey: string, cacheKey: string } {
    let prefix: string;
    let cachePrefix: string;
    
    switch (keyType) {
      case 'token':
        prefix = TOKEN_PREFIX;
        cachePrefix = `${CACHE_PREFIX}token_`;
        break;
      case 'userData':
        prefix = USER_DATA_PREFIX;
        cachePrefix = `${CACHE_PREFIX}userData_`;
        break;
      case 'expiry':
        prefix = `${TOKEN_PREFIX}expiry_`;
        cachePrefix = `${CACHE_PREFIX}expiry_`;
        break;
    }
    
    return {
      storageKey: `${prefix}${tenantCode}`,
      cacheKey: `${cachePrefix}${tenantCode}`
    };
  }

  private getCacheValue<T>(tenantCode: string, keyType: KeyType): T | null {
    const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
    return this.cacheService.getCache<T>(cacheKey, storageKey);
  }

  private setCacheValue<T>(tenantCode: string, keyType: KeyType, value: T, duration?: number): void {
    const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
    this.cacheService.setCache<T>(cacheKey, value, duration, storageKey);
  }

  private removeCacheValue(tenantCode: string, keyType: KeyType): void {
    const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
    this.cacheService.removeCache(cacheKey, storageKey);
  }


  private saveToStorage(token: string, userData?: UserData, remember?: boolean): void {
    // Always update memory state
    this.token = token;
    this.tenantCode = this.getTenantCode();
    if(!this.tenantCode) {
      return;
    }

    if (typeof window !== 'undefined') {
      try {
        // Save active tenant
        this.cacheService.setCache('active_tenant', this.tenantCode, undefined, ACTIVE_TENANT_KEY);

        // Calculate cache duration based on token expiry
        let cacheDuration: number;
        let tokenExpiryTime: number | null = null;
        
        // First, try to get expiry from userData
        if (userData?.expireDate) {
          tokenExpiryTime = new Date(userData.expireDate).getTime();
        } else {
          // If not in userData, try to decode from JWT token
          try {
            const tokenPayload = this.getUserFromToken(token);
            if (tokenPayload?.exp) {
              // JWT exp is in seconds, convert to milliseconds
              tokenExpiryTime = tokenPayload.exp * 1000;
            }
          } catch (error) {
            console.warn('Could not decode token expiry:', error);
          }
        }
        
        if (tokenExpiryTime) {
          const now = Date.now();
          const tokenDuration = Math.max(0, tokenExpiryTime - now);
          // Use the token's actual expiry time
          cacheDuration = tokenDuration;
        } else {
          // Fallback to hardcoded durations only if no expiry found
          cacheDuration = remember ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
        }
        
        // Store token and user data in cache
        this.setCacheValue(this.tenantCode, 'token', token, cacheDuration);
        this.setCacheValue(this.tenantCode, 'userData', userData, cacheDuration);

        // Set cookies for middleware to use
        if (typeof document !== 'undefined') {
          let cookieExpiry: Date | undefined;
          
          if (remember) {
            if (tokenExpiryTime) {
              // Use actual token expiry for remember me
              cookieExpiry = new Date(tokenExpiryTime);
            } else {
              // Fallback to cache duration if no token expiry
              cookieExpiry = new Date(Date.now() + cacheDuration);
            }
          }
          // For session (non-remember), cookieExpiry remains undefined (session cookie)
          
          const tokenKeys = this.getKeys(this.tenantCode, 'token');
          
          document.cookie = `${tokenKeys.storageKey}=${token}; path=/; ${cookieExpiry ? `expires=${cookieExpiry.toUTCString()};` : ''} SameSite=Lax`;
          
          // Store token expiry for validation
          if (userData?.expireDate) {
            const expiryKeys = this.getKeys(this.tenantCode, 'expiry');
            document.cookie = `${expiryKeys.storageKey}=${userData.expireDate}; path=/; ${cookieExpiry ? `expires=${cookieExpiry.toUTCString()};` : ''} SameSite=Lax`;
            
            // Cache expiry date
            this.setCacheValue(this.tenantCode, 'expiry', userData.expireDate, cacheDuration);
          }
        }

        // Setup token refresh for session-based auth (non-remember me)
        if (!remember && userData?.expireDate) {
          this.scheduleTokenRefresh(userData.expireDate);
        } else {
          this.clearTokenRefresh();
        }
      } catch (error) {
        console.error('Error saving auth data to storage:', error);
      }
    }
  }

  private scheduleTokenRefresh(expireDate: string): void {
    // Clear any existing refresh timeout
    this.clearTokenRefresh();
    this.refreshTokenRetryCount = 0;

    // Only setup refresh in browser environment
    if (typeof window !== 'undefined') {
      try {
        const expiryTime = new Date(expireDate).getTime();
        const now = Date.now();
        
        // Calculate time until refresh (3 minutes before expiry)
        const timeUntilRefresh = Math.max(0, expiryTime - now - TOKEN_REFRESH_BUFFER);
        
        if (timeUntilRefresh > 0) {
          this.refreshTokenTimeout = setTimeout(() => {
            this.performTokenRefresh();
          }, timeUntilRefresh);
        } else {
          // Token is already close to expiring or expired, refresh immediately
          this.performTokenRefresh();
        }
      } catch (error) {
        console.error('Error scheduling token refresh:', error);
      }
    }
  }

  private performTokenRefresh(): void {
    this.refreshToken()
      .then(response => {
        if (response.success) {
          // Reset retry count on success
          this.refreshTokenRetryCount = 0;
          console.log('Token refreshed successfully');
        } else {
          this.handleRefreshFailure();
        }
      })
      .catch(() => {
        this.handleRefreshFailure();
      });
  }

  private handleRefreshFailure(): void {
    this.refreshTokenRetryCount++;
    
    if (this.refreshTokenRetryCount <= TOKEN_REFRESH_MAX_RETRIES) {
      console.log(`Token refresh failed. Retrying (${this.refreshTokenRetryCount}/${TOKEN_REFRESH_MAX_RETRIES}) in ${TOKEN_REFRESH_RETRY_DELAY/1000}s`);
      
      // Schedule retry
      this.refreshTokenTimeout = setTimeout(() => {
        this.performTokenRefresh();
      }, TOKEN_REFRESH_RETRY_DELAY);
    } else {
      console.error('Token refresh failed after maximum retry attempts');
      // Reset retry count for next time
      this.refreshTokenRetryCount = 0;
    }
  }

  private clearTokenRefresh(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }


  private clearStorage(allTenants: boolean = false): void {
    // Clear token refresh
    this.clearTokenRefresh();

    if (typeof window !== 'undefined') {
      try {
        if (allTenants) {
          // Get all available tenants
          const availableTenants = this.getAvailableTenants();
          
          // Clear data for each tenant
          availableTenants.forEach(tenant => {
            // Remove from cache
            this.removeCacheValue(tenant, 'token');
            this.removeCacheValue(tenant, 'userData');
            this.removeCacheValue(tenant, 'expiry');
            
            // Clear cookies
            if (typeof document !== 'undefined') {
              const tokenKeys = this.getKeys(tenant, 'token');
              const expiryKeys = this.getKeys(tenant, 'expiry');
              document.cookie = `${tokenKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
              document.cookie = `${expiryKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
            }
          });
          
          // Remove active tenant
          this.cacheService.removeCache('active_tenant', ACTIVE_TENANT_KEY);
        } else {
          let tenantCode = this.getTenantCode();
          if(!tenantCode) {
            return;
          }

          // Clear only current tenant data
          this.removeCacheValue(tenantCode, 'token');
          this.removeCacheValue(tenantCode, 'userData');
          this.removeCacheValue(tenantCode, 'expiry');
          
          // Clear cookies
          if (typeof document !== 'undefined') {
            const tokenKeys = this.getKeys(tenantCode, 'token');
            const expiryKeys = this.getKeys(tenantCode, 'expiry');
            document.cookie = `${tokenKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
            document.cookie = `${expiryKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
          }
        }
      } catch (error) {
        console.error('Error clearing storage:', error);
      }
    }
  }


  /**
   * Initialize auth state from storage if available
   */
  public initFromStorage(): void {
    this.tenantCode = this.getTenantCode();
    if(!this.tenantCode) {
      return;
    }

    if (typeof window !== 'undefined') {
      try {
        // Get the active tenant from cache or use the current tenant code
        const activeTenant = this.cacheService.getCache<string>(
          'active_tenant', 
          ACTIVE_TENANT_KEY
        ) || this.tenantCode;
        
        // If tenant changed, update internal state
        if (activeTenant !== this.tenantCode) {
          this.tenantCode = activeTenant;
        }
        
        // Try to get token from cache
        const token = this.getCacheValue<string>(this.tenantCode, 'token');
        if (token) {
          this.token = token;
        }
      } catch (error) {
        console.error('Error initializing from storage:', error);
      }
    }
  }

  

  /**
   * Refresh the authentication token
   * @returns Promise with the refresh response
   */
  public async refreshToken(): Promise<AuthResponse> {
    try {
      if (!this.token) {
        return {
          success: false,
          error: 'No token to refresh'
        };
      }

      const response = await this.gsbService.refreshToken({}, this.token, this.tenantCode);

      if (response?.auth?.token) {
        const token = response.auth.token;
        
        // Get current user data
        const userData = this.getCurrentUser() || {};
        
        // Update expiry date if provided
        if (response.auth.expireDate) {
          userData.expireDate = response.auth.expireDate;
        }

        // Save with the same remember setting (false for auto-refresh)
        this.saveToStorage(token, userData, false);

        return {
          success: true,
          token,
          userData
        };
      } else {
        return {
          success: false,
          error: 'Token refresh failed: Invalid response'
        };
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token refresh failed'
      };
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    
    try {
      const userData = this.getCurrentUser();
      if (!userData || !userData.expireDate) {
        return false;
      }
      
      const expiryDate = new Date(userData.expireDate);
      const now = new Date();
      
      return expiryDate > now;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return false;
    }
  }

  /**
   * Authenticate a user with GSB
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {

      if(credentials.remember === undefined){
        credentials.remember = true;
      }

      credentials.includeUserInfo = true;

      if(!credentials?.variation?.tenantCode){
        if(!credentials?.variation){
          credentials.variation = {};
        }
        credentials.variation.tenantCode = this.tenantCode;
      }


      const response = await this.gsbService.getToken(credentials);

      if (response?.auth?.token) {
        const token = response.auth.token;
        
        const userData: UserData = {
          ...response.auth,
          roles: response.auth.roles || [],
          groups: response.auth.groups || []
        };


        this.saveToStorage(token, userData, credentials.remember);

        return {
          success: true,
          token,
          userData
        };
      } else {
        return {
          success: false,
          error: 'Authentication failed: Invalid token response'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  }

  /**
   * Log out the current user
   * @param allTenants Whether to logout from all tenants (default: false)
   */
  logout(allTenants: boolean = false): void {
    this.token = null;
    this.clearTokenRefresh();
    this.clearStorage(allTenants);
    this.cacheService.clearCache();
    setGsbToken(undefined);
  }

  /**
   * Set working tenant for multi-tenant support
   * @param tenantCode The tenant code to switch to
   * @param token Optional JWT token for the tenant (if not provided, attempts to load from storage)
   * @returns True if successful switch, false if failed (e.g., no token available)
   */
  setWorkingTenant(tenantCode: string, token?: string): boolean {
    // Clear existing refresh for previous tenant
    this.clearTokenRefresh();

    if (tenantCode === this.tenantCode && this.token) {
      // Already on this tenant with valid token
      return true;
    }

    // If token is provided, use it
    if (token) {
      this.tenantCode = tenantCode;
      this.token = token;
      
      // Save active tenant
      this.cacheService.setCache('active_tenant', tenantCode, undefined, ACTIVE_TENANT_KEY);
      
      // Cache the token
      this.setCacheValue(tenantCode, 'token', token);
      
      // Check if we need to setup token refresh for this tenant
      const userData = this.getCacheValue<UserData>(tenantCode, 'userData');
      if (userData && userData.expireDate && this.token) {
        // If expiry is less than 30 days, it's a session token (not remember me)
        const expiry = new Date(userData.expireDate).getTime();
        const now = Date.now();
        if ((expiry - now) < 30 * 24 * 60 * 60 * 1000) {
          this.scheduleTokenRefresh(userData.expireDate);
        }
      }
      
      return true;
    }
    
    // Try to get from cache
    const cachedToken = this.getCacheValue<string>(tenantCode, 'token');
    
    if (cachedToken) {
      this.tenantCode = tenantCode;
      this.token = cachedToken;
      
      // Save active tenant
      this.cacheService.setCache('active_tenant', tenantCode, undefined, ACTIVE_TENANT_KEY);
      
      // Check if we need to setup token refresh for this tenant
      const userData = this.getCacheValue<UserData>(tenantCode, 'userData');
      if (userData && userData.expireDate && this.token) {
        // If expiry is less than 30 days, it's a session token (not remember me)
        const expiry = new Date(userData.expireDate).getTime();
        const now = Date.now();
        if ((expiry - now) < 30 * 24 * 60 * 60 * 1000) {
          this.scheduleTokenRefresh(userData.expireDate);
        }
      }
      
      return true;
    }
    
    // No token available for this tenant
    return false;
  }

  /**
   * Get all available tenant codes from storage
   * @returns Array of tenant codes that have stored tokens
   */
  getAvailableTenants(): string[] {
    let tenantCode = this.getTenantCode();


    const availableTenants = new Set<string>();
    if(tenantCode) {
      availableTenants.add(tenantCode);
    }

    try {
      // Use cache service to find all tenants with tokens
      if (typeof localStorage !== 'undefined') {
        const localStorageKeys = Object.keys(localStorage);
        localStorageKeys.forEach(key => {
          if (key.startsWith(TOKEN_PREFIX) && !key.includes('expiry_')) {
            const tenantCode = key.replace(TOKEN_PREFIX, '');
            availableTenants.add(tenantCode);
          }
        });
      }
    } catch (error) {
      console.error('Error getting available tenants:', error);
    }

    return Array.from(availableTenants);
  }

  /**
   * Get user information from JWT token
   */
  getUserFromToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if the current user has a specific role
   */
  hasRole(role: string): boolean {
    if (!this.token) {
      return false;
    }

    const userData = this.getCurrentUser();
    if (!userData || !userData.roles) {
      return false;
    }
    return userData.roles.includes(role);
  }

  /**
   * Check if the current user has the admin role
   */
  isAdmin(): boolean {
    return this.hasRole(ADMIN_ROLE_ID);
  }

  /**
   * Get the subscription tier from the current user data
   */
  getSubscription(): SubscriptionType | undefined {
    const userData = this.getCurrentUser();
    return userData?.subscription;
  }

  /**
   * Get the current tenant code
   */
  getTenantCode(): string | undefined {
    if(!this.tenantCode) {
      this.tenantCode = getGsbTenantCode();
      if(this.tenantCode) {
        this.cacheService.setCache('tenantCode', this.tenantCode, undefined, this.tenantCode);
      }
    }
    return this.tenantCode;
  }

  /**
   * Get the current auth token
   */
  getToken(): string | null {
    let tenantCode = this.getTenantCode();
    if(!tenantCode) {
      return null;
    }

    if(this.token) {
      return this.token;
    }
    this.token = getEnvToken() || null;
    if (this.token) {
      this.cacheService.setCache('token', this.token, undefined, tenantCode);
      return this.token;
    }
    // If token not loaded yet, try to load from storage
    if (!this.token && typeof window !== 'undefined') {
      // Try cache
      const cachedToken = this.getCacheValue<string>(tenantCode, 'token');
      
      if (cachedToken) {
        this.token = cachedToken;
      }
    }
    
    return this.token;
  }


  setToken(token: string, userData?: UserData, remember: boolean = true): void {
    this.token = token;
    this.saveToStorage(token, userData ?? this.getCurrentUser() ?? {}, remember);
    setGsbToken(token);
  }

  async sendResetPasswordEmail(email: string): Promise<GsbSendResetPasswordEmailResponse> {
    const resp = await this.gsbService.runWfFunction({
      function: {
        name: 'sendResetPasswordEmail'
      },
      instance: {
        prms:{
          email
        }
      }
    }, this.token ?? '', this.tenantCode ?? '');
    if(resp?.response) {
      return resp.response;
    }
    return resp;
  } 

  async changePassword(oldPassword: string, newPassword: string,email?: string, resetToken?: string): Promise<GsbResponse> {
    return await this.gsbService.changePassword({
      oldPassword,
      newPassword,
      resetToken,
      email
    }, this.token ?? '', this.tenantCode ?? '');
  }

  /**
   * Get the current user data from storage
   */
  getCurrentUser(): UserData | null {
    if (typeof window === 'undefined') {
      return null;
    }

    if(!this.tenantCode) {
      this.tenantCode = getGsbTenantCode();
    }
    if(!this.tenantCode) {
      return null;
    }

    try {
      // Get from cache
      return this.getCacheValue<UserData>(this.tenantCode, 'userData');
    } catch (error) {
      console.error('Error getting user data from storage:', error);
      return null;
    }
  }
}
